import { z } from 'zod';
import { ageGroupsSchema, textSchema, timestampSchema } from '../task/task';
import { jobApprovalSchema, jobConfigIdSchema, jobTaskCountSchema } from './job';
import { stepModelChoicesSchema } from './stepModels';

/** A job configuration's target languages: only the job's language, or every language (QZ-D194). */
export const JOB_CONFIG_TARGET_LANGUAGES = ['job', 'all'] as const;

/** A job configuration's target languages, validated against `JOB_CONFIG_TARGET_LANGUAGES`. */
export const jobConfigTargetLanguagesSchema = z.enum(JOB_CONFIG_TARGET_LANGUAGES);
export type JobConfigTargetLanguages = z.infer<typeof jobConfigTargetLanguagesSchema>;

/**
 * `undefined` for a blank or whitespace-only string, so a description trims to nothing rather
 * than failing `textSchema`'s `min(1)` (QZ-D194).
 */
const blankToUndefined = (value: unknown): unknown =>
  typeof value === 'string' && value.trim() === '' ? undefined : value;

/**
 * A job configuration's optional description, dropped when blank after trimming. The inner
 * schema, not the preprocess wrapper, carries `.optional()`, so the blank-to-`undefined` output
 * still validates and `JobConfig['description']` stays `?: string` (Zod 4).
 */
const jobConfigDescriptionSchema = z.preprocess(blankToUndefined, textSchema.max(500).optional());

/**
 * An admin-made preset a GPS Quiz user's job runs on: step models, approval, target languages,
 * age groups and a task count limit (QZ-D194).
 */
export const jobConfigSchema = z
  .object({
    jobConfigId: jobConfigIdSchema,
    description: jobConfigDescriptionSchema,
    stepModels: stepModelChoicesSchema,
    approval: jobApprovalSchema,
    targetLanguages: jobConfigTargetLanguagesSchema,
    ageGroups: ageGroupsSchema,
    maxTaskCount: jobTaskCountSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .strip();
export type JobConfig = z.infer<typeof jobConfigSchema>;

/** The body accepted by `POST /job-configs` (QZ-D194). */
export const createJobConfigRequestSchema = z
  .object({
    jobConfigId: jobConfigIdSchema,
    description: jobConfigDescriptionSchema,
    stepModels: stepModelChoicesSchema,
    approval: jobApprovalSchema,
    targetLanguages: jobConfigTargetLanguagesSchema,
    ageGroups: ageGroupsSchema,
    maxTaskCount: jobTaskCountSchema,
  })
  .strict();
export type CreateJobConfigRequest = z.infer<typeof createJobConfigRequestSchema>;

/** The body accepted by `PUT /job-configs/:jobConfigId` (QZ-D194); the id cannot be renamed. */
export const updateJobConfigRequestSchema = createJobConfigRequestSchema.omit({
  jobConfigId: true,
});
export type UpdateJobConfigRequest = z.infer<typeof updateJobConfigRequestSchema>;

/** The body returned by `GET /job-configs` (QZ-D194). */
export const listJobConfigsResponseSchema = z
  .object({ jobConfigs: z.array(jobConfigSchema) })
  .strip();
export type ListJobConfigsResponse = z.infer<typeof listJobConfigsResponseSchema>;

/** The job configurations collection path (QZ-D194). */
export const JOB_CONFIGS_PATH = '/job-configs';
